import _camelCase from 'lodash/camelCase'
import _kebabCase from 'lodash/kebabCase'
import _startCase from 'lodash/startCase'
import _truncate from 'lodash/truncate'
import _upperFirst from 'lodash/upperFirst'

/**
 * Text utility functions
 *
 * Available functions:
 * - trimText: Trims text to a specified length
 * - kebabCase: Converts camelCase to kebab-case
 * - slugify: Converts a string to a URL-friendly slug
 * - slugifyToAttribute: Converts a string to an HTML-attribute-safe slug
 * - unslugify: Converts a slug to a title-cased string
 * - slugToComponentTypeName: Converts a slug to a component type name
 * - hashify: Creates a hyphenated string from text, removing special characters
 * - pascalCase: Converts a string to PascalCase
 * - dashify: Converts a string to kebab-case (with special handling)
 */

/**
 * Trim string of text to desired length
 * @param {string} text - text to trim
 * @param {number} maxLength - maxiumn length (characters)
 * @param {boolean} shouldTrim - case string should not be truncated
 * @returns {string} truncated to desired length
 */
export const trimText = (
  text: string = '',
  maxLength: number = 120,
  shouldTrim: boolean = true,
): string => {
  if (!shouldTrim) {
    return text
  }

  if (!text) {
    console.error('trimText: text is expected')
    return ''
  }

  return _truncate(text, {
    length: maxLength,
    omission: '...',
  })
}

/**
 * Convert from camelCase to kebab-case
 * @param {string} str - string to convert
 * @returns {string} kebab-case string
 */
export const kebabCase = function (str: string): string {
  if (!str) {
    console.error('kebabCase: str is expected')
    return ''
  }

  return _kebabCase(str)
}

/**
 * Converts a string to an HTML-attribute-safe slug
 * @param {string} str - string to convert
 * @returns {string} slugified string
 */
export const slugifyToAttribute = function (str: string): string {
  if (!str) {
    console.error('slugifyToAttribute: str is expected')
    return ''
  }

  return slugify(str)
    .replace(/^[0-9]+/g, '') // Remove leading numbers
    .replace(/^-/g, '') // Remove leading hyphen
}

/**
 * Converts a string to a URL-friendly slug
 * @param {string} str - string to convert
 * @returns {string} slugified string
 */
export const slugify = function (str: string): string {
  if (!str) {
    console.error('slugify: str is expected')
    return ''
  }

  return str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
}

/**
 * Converts a slug to a space-separated, title-cased string
 * @param {string} slug - slug to convert
 * @returns {string} unslugified string
 */
export const unslugify = function (slug: string): string {
  if (!slug) {
    console.error('unslugify: slug is expected')
    return ''
  }

  // First replace hyphens with spaces, then convert to title case using startCase
  return _startCase(slug.replace(/-/g, ' '))
}

/**
 * Converts a slug to a component type name
 * @param {string} slug - slug to convert
 * @param {string} suffix - optional suffix to add to the component type name
 * @returns {string} component type name *
 */
export const slugToComponentTypeName = function (
  slug: string,
  suffix: string = '',
): string {
  try {
    throwableSlugValidate(slug, suffix)
  } catch (err) {
    console.error(err)
    // we'll attempt to carry on without a slug... but this could cause issues?
  }

  return unslugify(slug).replace(/\W/g, '') + suffix
}

const throwableSlugValidate = function (
  slug: string,
  suffix: string = '',
): void {
  if (!slug) {
    throw new Error(
      `slugToComponentTypeName: slug is expected - input was { slug: "${slug}", suffix: "${suffix}" }`,
    )
  }
}

/**
 * Creates a simple hash of the text
 * @param {string} text - text to hashify
 * @returns {string} hashified string
 */
export const hashify = (text: string = ''): string => {
  if (!text) {
    console.error('hashify: text is expected')
    return ''
  }
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16)
}

/**
 * Converts a string to PascalCase
 * @param {string} value - value to convert
 * @returns {string} PascalCase string
 */
export const pascalCase = function (value: string = ''): string {
  if (!value) {
    console.error('pascalCase: value is expected')
    return ''
  }

  return _upperFirst(_camelCase(value))
}
